/** Criptografia ponta a ponta: chaves do aparelho e cifragem de mensagem.
 *
 * O servidor guarda hoje 1091 mensagens com o texto em claro — verificado no
 * banco em 7/ago/2026. Quem alcança o servidor lê tudo. Isto muda o alvo: o
 * servidor passa a guardar uma caixa que não abre.
 *
 * ESCOLHA DE ALGORITMO — ECDH P-256 + AES-GCM, e não X25519.
 * X25519 é mais moderno e mais elegante, mas o suporte em WebView de Android
 * antigo ainda é irregular, e é exatamente ali que o app roda. P-256 existe em
 * todo lugar que tenha WebCrypto, e aceita chave privada **não-extraível** —
 * `extractable: false` faz o próprio navegador recusar exportá-la, então nem
 * código injetado nesta página consegue ler. Segurança equivalente na prática.
 *
 * O QUE CONTINUA EM CLARO, e não há como ser diferente: `from`, `to`, `ts` e
 * `id`. O servidor precisa deles para entregar. Ele deixa de ver o QUE foi
 * dito, mas continua vendo COM QUEM e QUANDO — verdade em qualquer app do
 * gênero, e que precisa ser dita quando isso for anunciado ao usuário.
 */

const DB = "wuwei-cripto";
const LOJA = "chaves";
const ID_PAR = "par-do-aparelho";

/** IndexedDB e não localStorage: `CryptoKey` não sobrevive a `JSON.stringify`.
 *  Guardar o objeto direto é o que preserva o `extractable: false` — passar por
 *  texto exigiria exportar a chave, que é justamente o que não queremos. */
function abrirBanco(): Promise<IDBDatabase> {
  return new Promise((ok, erro) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(LOJA)) req.result.createObjectStore(LOJA);
    };
    req.onsuccess = () => ok(req.result);
    req.onerror = () => erro(req.error);
  });
}

function ler<T>(chave: string): Promise<T | null> {
  return abrirBanco().then(
    (db) =>
      new Promise((ok, erro) => {
        const req = db.transaction(LOJA, "readonly").objectStore(LOJA).get(chave);
        req.onsuccess = () => ok((req.result as T) ?? null);
        req.onerror = () => erro(req.error);
      }),
  );
}

function gravar(chave: string, valor: unknown): Promise<void> {
  return abrirBanco().then(
    (db) =>
      new Promise((ok, erro) => {
        const req = db.transaction(LOJA, "readwrite").objectStore(LOJA).put(valor, chave);
        req.onsuccess = () => ok();
        req.onerror = () => erro(req.error);
      }),
  );
}

export type ParDoAparelho = {
  privada: CryptoKey;
  publica: CryptoKey;
  /** A pública em base64url, pronta para publicar no servidor. */
  publicaB64: string;
};

const b64 = (b: ArrayBuffer) =>
  btoa(String.fromCharCode(...new Uint8Array(b)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

const deB64 = (s: string) => {
  const p = s.replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(p + "=".repeat((4 - (p.length % 4)) % 4));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
};

/** O par deste aparelho, criado na primeira vez e reusado depois.
 *
 *  A privada nasce com `extractable: false` e é gravada como `CryptoKey`. Se
 *  este banco for apagado, as mensagens cifradas para este aparelho tornam-se
 *  ilegíveis para sempre — não existe recuperação, e isso é a consequência
 *  direta de não haver cópia da chave em lugar nenhum. */
export async function parDoAparelho(): Promise<ParDoAparelho> {
  const guardado = await ler<{ privada: CryptoKey; publica: CryptoKey }>(ID_PAR);
  if (guardado?.privada && guardado?.publica) {
    const bruta = await crypto.subtle.exportKey("raw", guardado.publica);
    return { ...guardado, publicaB64: b64(bruta) };
  }
  const par = await crypto.subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, false, [
    "deriveKey",
  ]);
  await gravar(ID_PAR, { privada: par.privateKey, publica: par.publicKey });
  const bruta = await crypto.subtle.exportKey("raw", par.publicKey);
  return { privada: par.privateKey, publica: par.publicKey, publicaB64: b64(bruta) };
}

/** Deriva a chave de conversa a partir da minha privada e da pública do outro.
 *
 *  Os dois lados chegam ao mesmo segredo sem que ele trafegue — é o ponto do
 *  ECDH. O `salt` é fixo por par de chaves porque a variação por mensagem vem
 *  do IV do AES-GCM, que é sorteado a cada envio. */
async function chaveDeConversa(minhaPrivada: CryptoKey, publicaDoOutroB64: string) {
  const publicaDoOutro = await crypto.subtle.importKey(
    "raw",
    deB64(publicaDoOutroB64),
    { name: "ECDH", namedCurve: "P-256" },
    false,
    [],
  );
  return crypto.subtle.deriveKey(
    { name: "ECDH", public: publicaDoOutro },
    minhaPrivada,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

/** Formato do que vai no lugar do texto. `v` existe para que uma mudança de
 *  esquema no futuro não faça o destinatário tentar abrir com a regra errada. */
export type Cifrado = { v: 1; iv: string; dados: string };

/** Uma caixa por aparelho, endereçada pelo id do aparelho.
 *
 *  Cifrar para um aparelho só quebra quem usa celular E navegador: o segundo
 *  nunca abriria. Inclui também os aparelhos de QUEM ENVIA — sem isso, a
 *  própria pessoa não lê no outro aparelho o que mandou daqui, que é o eco
 *  multi-device que o app já fazia antes da criptografia existir. */
export type Envelope = { v: 1; de: string; caixas: Record<string, Cifrado> };

export function pareceEnvelope(x: unknown): x is Envelope {
  const e = x as Envelope;
  return (
    typeof x === "object" && x !== null && e.v === 1 &&
    typeof e.de === "string" && typeof e.caixas === "object" && e.caixas !== null
  );
}

/** Fecha o texto para vários aparelhos de uma vez.
 *
 *  Devolve `null` quando não há para quem cifrar — e aí quem chama manda em
 *  claro, que é o que mantém a conversa viva com quem ainda não publicou
 *  chave. */
export async function cifrarParaTodos(
  texto: string,
  minhaPrivada: CryptoKey,
  meuAparelho: string,
  destinos: { device: string; publicKey: string }[],
): Promise<Envelope | null> {
  const caixas: Record<string, Cifrado> = {};
  for (const d of destinos) {
    try {
      caixas[d.device] = await cifrar(texto, minhaPrivada, d.publicKey);
    } catch {
      // Chave inválida de um aparelho não pode impedir os outros de receber.
    }
  }
  return Object.keys(caixas).length ? { v: 1, de: meuAparelho, caixas } : null;
}

/** Abre a caixa endereçada a ESTE aparelho. */
export async function abrirEnvelope(
  env: Envelope,
  minhaPrivada: CryptoKey,
  meuAparelho: string,
  chavesDoRemetente: { device: string; publicKey: string }[],
): Promise<string | null> {
  const minha = env.caixas[meuAparelho];
  if (!minha) return null;
  // A pública usada na derivação é a do aparelho que enviou — `env.de` diz qual
  // é, e sem isso teríamos de tentar todas às cegas.
  const doRemetente = chavesDoRemetente.find((c) => c.device === env.de);
  const candidatas = doRemetente ? [doRemetente, ...chavesDoRemetente] : chavesDoRemetente;
  for (const c of candidatas) {
    try {
      return await decifrar(minha, minhaPrivada, c.publicKey);
    } catch {
      /* próxima */
    }
  }
  return null;
}

export async function cifrar(
  texto: string,
  minhaPrivada: CryptoKey,
  publicaDoOutroB64: string,
): Promise<Cifrado> {
  const chave = await chaveDeConversa(minhaPrivada, publicaDoOutroB64);
  // IV novo a cada mensagem: repetir IV com a mesma chave quebra o AES-GCM.
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const dados = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    chave,
    new TextEncoder().encode(texto),
  );
  return { v: 1, iv: b64(iv.buffer), dados: b64(dados) };
}

export async function decifrar(
  c: Cifrado,
  minhaPrivada: CryptoKey,
  publicaDoOutroB64: string,
): Promise<string> {
  if (c.v !== 1) throw new Error(`versão de cifragem desconhecida: ${c.v}`);
  const chave = await chaveDeConversa(minhaPrivada, publicaDoOutroB64);
  const claro = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: deB64(c.iv) },
    chave,
    deB64(c.dados),
  );
  return new TextDecoder().decode(claro);
}

/** Cifra bytes (anexo, áudio) para vários aparelhos.
 *
 *  Cifra os BYTES, não o base64. Cifrar o base64 e re-encodar somaria 33% duas
 *  vezes: um anexo de 8 MB chegaria perto de 11 MB e poderia estourar o limite
 *  de mensagem. Decodificando antes, o resultado final fica do tamanho do
 *  original.
 *
 *  Uma caixa por aparelho como no texto — mas o CONTEÚDO é cifrado uma vez só,
 *  com uma chave sorteada, e é essa chave que vai cifrada em cada caixa. Sem
 *  isso, um anexo de 8 MB seria repetido inteiro para cada aparelho. */
export type EnvelopeBinario = {
  v: 1;
  de: string;
  /** A chave do conteúdo, cifrada uma vez por aparelho. */
  chaves: Record<string, Cifrado>;
  iv: string;
  /** O conteúdo, cifrado uma vez só. */
  dados: string;
};

export function pareceEnvelopeBinario(x: unknown): x is EnvelopeBinario {
  const e = x as EnvelopeBinario;
  return (
    typeof x === "object" && x !== null && e.v === 1 &&
    typeof e.dados === "string" && typeof e.iv === "string" &&
    typeof e.chaves === "object" && e.chaves !== null
  );
}

export async function cifrarBase64ParaTodos(
  base64: string,
  minhaPrivada: CryptoKey,
  meuAparelho: string,
  destinos: { device: string; publicKey: string }[],
): Promise<EnvelopeBinario | null> {
  if (!destinos.length) return null;
  const bytes = deB64(base64.includes(",") ? base64.split(",")[1]! : base64);
  const chaveConteudo = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, [
    "encrypt",
    "decrypt",
  ]);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const cifradoConteudo = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, chaveConteudo, bytes);
  const chaveCrua = await crypto.subtle.exportKey("raw", chaveConteudo);
  const chaveB64 = b64(chaveCrua);

  const chaves: Record<string, Cifrado> = {};
  for (const d of destinos) {
    try {
      chaves[d.device] = await cifrar(chaveB64, minhaPrivada, d.publicKey);
    } catch {
      /* aparelho com chave inválida não impede os outros */
    }
  }
  if (!Object.keys(chaves).length) return null;
  return { v: 1, de: meuAparelho, chaves, iv: b64(iv.buffer), dados: b64(cifradoConteudo) };
}

export async function abrirEnvelopeBinario(
  env: EnvelopeBinario,
  minhaPrivada: CryptoKey,
  meuAparelho: string,
  chavesDoRemetente: { device: string; publicKey: string }[],
): Promise<string | null> {
  const minha = env.chaves[meuAparelho];
  if (!minha) return null;
  const doRemetente = chavesDoRemetente.find((c) => c.device === env.de);
  const candidatas = doRemetente ? [doRemetente, ...chavesDoRemetente] : chavesDoRemetente;
  for (const c of candidatas) {
    try {
      const chaveB64 = await decifrar(minha, minhaPrivada, c.publicKey);
      const chaveConteudo = await crypto.subtle.importKey(
        "raw",
        deB64(chaveB64),
        { name: "AES-GCM" },
        false,
        ["decrypt"],
      );
      const claro = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: deB64(env.iv) },
        chaveConteudo,
        deB64(env.dados),
      );
      return b64(claro);
    } catch {
      /* próxima */
    }
  }
  return null;
}

/** Reconhece o que é caixa fechada. Mensagem antiga — as 1091 que já existem em
 *  claro — não tem este formato e continua sendo lida como texto comum. */
export function pareceCifrado(x: unknown): x is Cifrado {
  return (
    typeof x === "object" &&
    x !== null &&
    (x as Cifrado).v === 1 &&
    typeof (x as Cifrado).iv === "string" &&
    typeof (x as Cifrado).dados === "string"
  );
}

/** Identidade deste aparelho, estável entre sessões.
 *
 *  Serve para o servidor distinguir celular de navegador na mesma conta: são
 *  dois pares de chaves, e quem envia precisa cifrar uma vez para cada um. */
export function idDoAparelho(): string {
  const CH = "wuwei.device";
  let id = localStorage.getItem(CH);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(CH, id);
  }
  return id;
}

/** Publica a chave pública deste aparelho. Idempotente: republicar a mesma
 *  chave é inofensivo, e republicar é o que conserta o caso de o aparelho ter
 *  perdido o IndexedDB e gerado par novo. */
export async function publicarChave(session: string): Promise<boolean> {
  try {
    const par = await parDoAparelho();
    const r = await fetch("/chaves", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session, device: idDoAparelho(), publicKey: par.publicaB64 }),
    });
    return r.ok;
  } catch {
    return false;
  }
}

/** Cache das chaves alheias, curto de propósito.
 *
 *  Cache longo demais é perigoso: se a pessoa troca de aparelho, continuamos
 *  cifrando para uma chave que ninguém mais consegue abrir. Um minuto é o
 *  suficiente para não pedir a cada mensagem e curto para o conserto chegar. */
const cacheChaves = new Map<string, { quando: number; devices: { device: string; publicKey: string }[] }>();
const VALIDADE_MS = 60_000;

export async function chavesDe(
  email: string,
): Promise<{ device: string; publicKey: string }[]> {
  const guardado = cacheChaves.get(email);
  if (guardado && Date.now() - guardado.quando < VALIDADE_MS) return guardado.devices;
  try {
    const r = await fetch(`/chaves/${encodeURIComponent(email)}`);
    if (!r.ok) return [];
    const d = (await r.json()) as { devices?: { device: string; publicKey: string }[] };
    const devices = d.devices ?? [];
    cacheChaves.set(email, { quando: Date.now(), devices });
    return devices;
  } catch {
    // Sem rede, devolver vazio faz a mensagem sair em claro — que é pior em
    // segurança e melhor em entrega. Quem chama decide; ver `enviarOuEnfileirar`.
    return [];
  }
}

/** Pendurado no `window` para os testes de ponta a ponta e para diagnóstico.
 *
 *  Expor as FUNÇÕES não enfraquece nada: o algoritmo é público por princípio, e
 *  a chave privada continua `extractable: false` — quem chamar daqui consegue
 *  cifrar e decifrar nesta aba, que é exatamente o que a própria aba já podia
 *  fazer. O que não se expõe, e não se deve expor nunca, é caminho para
 *  exportar a privada: ele não existe.
 */
declare global {
  interface Window {
    __wuweiCripto?: {
      parDoAparelho: typeof parDoAparelho;
      cifrar: typeof cifrar;
      decifrar: typeof decifrar;
      pareceCifrado: typeof pareceCifrado;
      cifrarParaTodos: typeof cifrarParaTodos;
      abrirEnvelope: typeof abrirEnvelope;
      pareceEnvelope: typeof pareceEnvelope;
      cifrarBase64ParaTodos: typeof cifrarBase64ParaTodos;
      abrirEnvelopeBinario: typeof abrirEnvelopeBinario;
      pareceEnvelopeBinario: typeof pareceEnvelopeBinario;
      idDoAparelho: typeof idDoAparelho;
      publicarChave: typeof publicarChave;
      chavesDe: typeof chavesDe;
    };
  }
}

if (typeof window !== "undefined") {
  window.__wuweiCripto = {
    parDoAparelho, cifrar, decifrar, pareceCifrado,
    cifrarParaTodos, abrirEnvelope, pareceEnvelope,
    cifrarBase64ParaTodos, abrirEnvelopeBinario, pareceEnvelopeBinario,
    idDoAparelho, publicarChave, chavesDe,
  };
}
